import React, { useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  FloatingFeature,
} from "@models";
import { 
  CHAR_FEATURE_TYPES
} from "@dndModels/Constants";

import API from "@dndUtilities/system_api";

import StringBox from "@components/input/strings/StringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import SelectStringBox from "@components/input/selects/SelectStringBox"; 
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  name?: string;
  limit?: number;
  default_search?: string;
  feature_type?: string;
  allowed_feature_types?: string[];
  floating_feature_id: string;
  onFloatingFeatureSelected: (floating_feature: FloatingFeature) => void;
}

const api = API.getInstance();

export default function FloatingFeatureBox({
  name = "Selected Floating Feature",
  limit = 100,
  default_search = "",
  feature_type: props_feature_type = "Any",
  floating_feature_id: props_floating_feature_id,
  allowed_feature_types = CHAR_FEATURE_TYPES,
  onFloatingFeatureSelected
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [selected_floating_feature, setSelectedFloatingFeature] = useState<FloatingFeature | null>(null);
  const [filter, setFilter] = useState<any>({});
  const [feature_type, setFeatureType] = useState(props_feature_type);
  const [search_string, setSearchString] = useState(default_search);

  const load = useCallback( async () => {
    let res = await api.getObjects<FloatingFeature>({ data_type: "floating_feature" });
    if (allowed_feature_types.includes("Creature Ability")) {
      const res2 = await api.getObjects<FloatingFeature>({ data_type: "creature_feature" });
      res.push(...res2);
    }
    const all_floating_features = res as FloatingFeature[];
    let selected_ff: FloatingFeature | null = null;
    const ff_finder = all_floating_features.filter(o => o._id === props_floating_feature_id);
    if (ff_finder.length === 1) {
      selected_ff = ff_finder[0];
    }
    if (selected_ff) {
      setFeatureType(selected_ff.feature_type);
      setSearchString(selected_ff.name);
      setSelectedFloatingFeature(selected_ff);
      setReloading(true);
    } else if (allowed_feature_types.length !== CHAR_FEATURE_TYPES.length) {
      setReloading(true);
    }
    setLoading(false);
  }, [allowed_feature_types, props_floating_feature_id]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  useEffect(() => {
    if (reloading) {
      updateFilter();
      setReloading(false);
    }
    // eslint-disable-next-line
  }, [reloading]);

  const updateFilter = () => {
    const new_filter: any = {};
    
    if (feature_type !== "Any") {
      new_filter.feature_type = feature_type;
    } else if (allowed_feature_types.length !== CHAR_FEATURE_TYPES.length) {
      new_filter.feature_type = { "in": allowed_feature_types };
    }
    if (search_string !== "") {
      const pieces = search_string.split("|");
      new_filter["and"] = pieces.map(o => {
        return { "or": [{ "name*includes": o }, { "description*includes": o }]};
      });
    }

    setFilter(new_filter);
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        { props_feature_type === "Any" &&
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox 
              name="Feature Type"
              options={["Any",...allowed_feature_types.sort((a,b) => { return a.localeCompare(b); })]}
              value={feature_type} 
              onChange={(value: string) => {
                setFeatureType(value);
                setReloading(true);
              }}
            />
          </Grid>
        }
        <Grid
          size={{
            xs: props_feature_type === "Any" ? 6 : 12
          }}>
          <StringBox 
            value={search_string} 
            name="Search"
            onBlur={(value: string) => {
              setSearchString(value);
              setReloading(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { !reloading && 
            <SelectObjectBox
              name={name}
              data_types={allowed_feature_types.includes("Creature Ability") ? ["floating_feature","creature_feature"] : ["floating_feature"]}
              filter={filter}
              limit={limit}
              // include_description
              display_format="{obj.name} ({obj.description})"
              value={ selected_floating_feature ? selected_floating_feature._id : "" }
              auto_sort
              // log_it
              onChange={(id: string) => {
                let new_selected = api.get_floating_feature(id);
                if (!new_selected) {
                  new_selected = api.get_creature_feature(id);
                }
                if (new_selected) {
                  onFloatingFeatureSelected(new_selected);
                }
                setSelectedFloatingFeature(new_selected);
              }}
            />
          }
        </Grid>
      </Grid>
    );
  }
}
